import React, { useState, useEffect } from 'react';
import { Award, Trophy, Star, Lock, CheckCircle2 } from 'lucide-react';
import { getAllAchievements, getMyAchievements, getTenantRanking } from '../services/attendanceApi';
import { useLanguage } from '../contexts/LanguageContext';

function AchievementsPage() {
  const { t } = useLanguage();
  
  const [allAchievements, setAllAchievements] = useState([]);
  const [myAchievements, setMyAchievements] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [allRes, myRes, rankingRes] = await Promise.all([
        getAllAchievements(),
        getMyAchievements(),
        getTenantRanking(10)
      ]);

      setAllAchievements(allRes.data);
      setMyAchievements(myRes.data.achievements || []);
      setRanking(rankingRes.data);
    } catch (err) {
      console.error('Erro ao carregar conquistas:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasAchievement = (code) => {
    return myAchievements.some(a => a.code === code);
  };

  const categoryLabels = {
    speed: { label: t('achievements.categorySpeed'), color: 'from-yellow-400 to-orange-500', icon: '⚡' },
    volume: { label: t('achievements.categoryVolume'), color: 'from-blue-400 to-blue-600', icon: '📊' },
    quality: { label: t('achievements.categoryQuality'), color: 'from-green-400 to-green-600', icon: '⭐' },
    conversion: { label: t('achievements.categoryConversion'), color: 'from-purple-400 to-purple-600', icon: '🎯' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const totalPoints = myAchievements.reduce((sum, a) => sum + (a.points_earned || 0), 0);
  const currentUserId = JSON.parse(atob(localStorage.getItem('token').split('.')[1])).id;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
          <Trophy size={28} className="text-yellow-500" />
          {t('achievements.title')}
        </h1>
        <p className="text-gray-500 dark:text-slate-400">{t('achievements.subtitle')}</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
          <Award size={32} className="mb-2 opacity-80" />
          <p className="text-4xl font-bold">{myAchievements.length}</p>
          <p className="text-sm opacity-90">{t('achievements.medalsWon')}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
          <Star size={32} className="mb-2 opacity-80" />
          <p className="text-4xl font-bold">{totalPoints}</p>
          <p className="text-sm opacity-90">{t('achievements.totalPoints')}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
          <Trophy size={32} className="mb-2 opacity-80" />
          <p className="text-4xl font-bold">
            #{ranking.findIndex(r => r.user_id === currentUserId) + 1 || '—'}
          </p>
          <p className="text-sm opacity-90">{t('achievements.rankingPosition')}</p>
        </div>
      </div>

      {/* Ranking */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Trophy size={20} className="text-yellow-500" />
            {t('achievements.topMonth')}
          </h2>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {ranking.slice(0, 10).map((user, idx) => {
            const isMe = user.user_id === currentUserId;
            return (
              <div key={user.user_id} className={`p-4 flex items-center gap-4 ${isMe ? 'bg-yellow-50 dark:bg-yellow-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                <div className="w-10 text-center">
                  {idx === 0 && <Trophy size={24} className="text-yellow-500 mx-auto" />}
                  {idx === 1 && <Trophy size={24} className="text-gray-400 mx-auto" />}
                  {idx === 2 && <Trophy size={24} className="text-orange-600 mx-auto" />}
                  {idx > 2 && <span className="text-gray-500 font-bold">#{idx + 1}</span>}
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  {user.user_name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-slate-100">
                    {user.user_name} {isMe && <span className="text-xs text-yellow-600 dark:text-yellow-400">({t('achievements.you')})</span>}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">{user.total_achievements} {t('achievements.achievementsLower')}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{user.total_points}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">{t('achievements.points')}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Conquistas por Categoria */}
      {Object.entries(categoryLabels).map(([category, info]) => {
        const categoryAchievements = allAchievements.filter(a => a.category === category);
        if (categoryAchievements.length === 0) return null;

        return (
          <div key={category} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className={`p-5 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r ${info.color} text-white`}>
              <h2 className="text-lg font-bold flex items-center gap-2">
                <span className="text-2xl">{info.icon}</span>
                {info.label}
              </h2>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryAchievements.map(achievement => {
                const earned = hasAchievement(achievement.code);
                return (
                  <div 
                    key={achievement.id} 
                    className={`relative rounded-xl p-4 border-2 transition-all ${
                      earned 
                        ? 'bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-yellow-300 dark:border-yellow-700 shadow-md' 
                        : 'bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 opacity-60'
                    }`}
                  >
                    {earned && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle2 size={20} className="text-green-600 dark:text-green-400" />
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <div className={`text-4xl ${!earned && 'grayscale'}`}>
                        {achievement.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 dark:text-slate-100 text-sm">
                          {achievement.name}
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                          {achievement.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400">
                            +{achievement.points} {t('achievements.pts')}
                          </span>
                          {earned && (
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                              ✓ {t('achievements.won')}
                            </span>
                          )}
                          {!earned && (
                            <span className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                              <Lock size={10} /> {t('achievements.locked')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default AchievementsPage;